import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Dialog, DialogContent, styled } from "@mui/material";
import { CustomMessageInput } from "./UI/CustomMessageInput";
import useSessionStore from "../context/useSessionStore";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import useScrollDetect, {
  scrollToObject,
  useAutoScroll,
} from "../utils/useScrollDetect";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import { ChatHistory } from "./ChatHistory";
import ChatContainer from "./UI/ChatContainer";
import { MemoizedAnswer } from "./Answer";
import { clinicScrollableStyle } from "fe-common/ui/commonStyles";
import useStreamManager from "../wsocket/wsocket";
import { useParams } from "react-router-dom";
import { MemoizedQuestion } from "./Question";
import { NewSessionInput } from "./UI/NewSessionInput";
import { MessageType, QuestionTypes, SessionType } from "../types/types";

//region [[ Styles ]]
const ChatMessagesScroll = styled("div")({
  overflow: "auto",
  ...clinicScrollableStyle,
});
const ChatMessagesHolder = styled("div")<{
  embedded?: boolean;
  general?: boolean;
}>(({ theme, embedded, general }) => ({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  minHeight: "100%",
  width: "100%",
  maxWidth: 800,
  margin: "auto",
  padding: embedded
    ? "20px 12px"
    : general
    ? "20px 12px 80px 12px"
    : "120px 12px 80px 12px",
  gap: 20,
  [theme.breakpoints.down("sm")]: {
    paddingTop: 20,
    paddingBottom: 20,
  },
}));

const InputContainer = styled("div")<{ embedded?: boolean }>(
  ({ theme, embedded }) => ({
    borderTop: "1px solid " + theme.palette.divider,
    padding: embedded ? "16px 20px" : "16px 34px 20px 34px",
    [theme.breakpoints.down("md")]: {
      padding: "16px 14px 20px 14px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "10px 14px 12px 14px",
    },
  }),
);

const SessionLoading = styled("div")({
  marginTop: 20,
  textAlign: "center",
  height: 34,
});

//endregion [[ Styles ]]

//region [[ Props ]]

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ChatMain = () => {
  const {
    sessionId,
    sessionUser,
    patientSummary,
    confirmRequired,
    ragSettings,
    chatMessages,
    addQuestion,
    loadGuideline,
    insights,
    viewSettings,
    setStreamManager,
    replyIsLoading,
    loadingChatSession,
    sessionType,
    setSessionType,
  } = useSessionStore();
  const allowDebug = sessionUser?.allowDebug;
  const reporter = useEventReportedContext();
  const rtlDisplay = ragSettings?.rtlDisplay;
  const embedded = ragSettings?.embeddedView;
  const showChatHistory = ragSettings?.showChatHistory;
  const msgListRef: React.MutableRefObject<HTMLDivElement> = useRef();
  const [autoScrollOn, autoScrollOff] = useAutoScroll(msgListRef.current);
  const [debugQuestion, setDebugQuestion] = useState(null);
  const { sessionType: paramsSessionType } = useParams();
  const chatStreamManager = useStreamManager(
    ragSettings.wssEndpoint,
    allowDebug,
  );

  const showInsights = useMemo(
    () =>
      sessionType === SessionType.PATIENT &&
      sessionUser?.allowDebug &&
      !!insights,
    [sessionType, sessionUser, insights],
  );

  const showSessionInput = useMemo(() => {
    if (viewSettings.persistMobileView) return true;
    if (loadingChatSession || embedded || confirmRequired) return false;
    return (
      sessionType === SessionType.PATIENT ||
      (sessionType === SessionType.GENERAL && !sessionId)
    );
  }, [
    loadingChatSession,
    viewSettings.persistMobileView,
    confirmRequired,
    embedded,
    sessionType,
    sessionId,
  ]);

  useEffect(() => {
    if (paramsSessionType) setSessionType(paramsSessionType as SessionType);
  }, [setSessionType, paramsSessionType]);

  useEffect(() => {
    setStreamManager(chatStreamManager);
  }, [chatStreamManager, setStreamManager]);

  useScrollDetect(
    msgListRef.current,
    (scrollAmount) => {
      if (scrollAmount < -25) {
        autoScrollOff(); //stop autoscroll when user scrolls up
      }
    },
    null,
    () => {
      autoScrollOn(); //activate autoscroll when user scrolls to bottom
    },
  );

  const onEnter = useCallback(
    (text: string) => {
      if (text.trim()) {
        autoScrollOn();
        reporter?.reportEvent(MIX_PANEL_EVENTS.QUESTION, {
          question: text,
          generateSuggestions: ragSettings.generateSuggestions,
        });
        addQuestion(text, (answer) => {
          reporter?.reportAnswer(answer);
        });
      }
    },
    [autoScrollOn, reporter, ragSettings.generateSuggestions, addQuestion],
  );

  return (
    <ChatContainer embedded={embedded} rtlDisplay={rtlDisplay}>
      <Dialog
        open={!!(allowDebug && debugQuestion)}
        fullWidth
        onClose={() => setDebugQuestion(null)}
      >
        <DialogContent>
          <pre>
            <code>{JSON.stringify(debugQuestion, null, 4)}</code>
          </pre>
        </DialogContent>
      </Dialog>
      <ChatMessagesScroll ref={msgListRef} id={"message-list"}>
        <ChatMessagesHolder
          embedded={embedded}
          general={sessionType === SessionType.GENERAL || showInsights}
          className={"chat-container"}
        >
          {loadingChatSession && (
            <SessionLoading>
              <CircularProgress size={30} />
            </SessionLoading>
          )}

          {showSessionInput && (
            <NewSessionInput
              sessionType={sessionType}
              withTopNav={!sessionId}
              withDisclaimer={true}
              insights={showInsights && insights}
              rtlDisplay={rtlDisplay}
            />
          )}

          {showChatHistory && <ChatHistory />}
          {chatMessages?.map((msg, idx) => {
            if (msg.type === MessageType.question)
              return (
                <MemoizedQuestion
                  msg={msg}
                  key={"question" + idx}
                  onUpdate={null}
                />
              );

            return (
              <MemoizedAnswer
                message={msg}
                streamManager={chatStreamManager}
                debugMode={ragSettings.debugMode}
                key={"answer" + idx}
                question={chatMessages[idx - 1]?.text}
                getGuideline={loadGuideline}
                questionType={QuestionTypes.Chat}
                onSuggestedQuestion={() => {
                  autoScrollOn();
                }}
                onGuidelineChange={(messageObject) => {
                  scrollToObject(msgListRef.current, messageObject);
                }}
                onAvatarClick={() => setDebugQuestion(msg)}
              />
            );
          })}
        </ChatMessagesHolder>
      </ChatMessagesScroll>
      <InputContainer embedded={embedded} dir={rtlDisplay ? "rtl" : ""}>
        <CustomMessageInput
          disabled={
            replyIsLoading(QuestionTypes.Chat) ||
            (sessionType === SessionType.PATIENT && !patientSummary) ||
            (sessionType === SessionType.GENERAL && !sessionId)
          }
          onSend={onEnter}
        />
      </InputContainer>
    </ChatContainer>
  );
};
